import { useCallback, useEffect, useMemo } from 'react'

import { isSmartAccount } from '@ambire-common/libs/account/account'
import { getIsViewOnly } from '@ambire-common/utils/accounts'
import type {
  DcaCancelStatus,
  DcaDetailStatus,
  DcaFlowStep,
  DcaOrdersStatus
} from '@web/extension-services/background/controllers/dca/dca'
import type { DcaErrorCode } from '@web/extension-services/background/controllers/dca/errors'
import {
  DcaCancelSummary,
  DcaExecution,
  DcaOrder,
  DcaOrderDetail,
  DcaPendingSubmission,
  DcaToken
} from '@web/extension-services/background/controllers/dca/types'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useControllerState from '@web/hooks/useControllerState'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { DCA_CHAIN_ID } from '@web/modules/swap-and-bridge/hooks/useDca'

/** The keeper scans every 15 s and the indexer waits 12 blocks: no point polling faster. */
const LIST_POLL_MS = 20000
const DETAIL_POLL_MS = 15000

const same = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

/**
 * Activity → DCA: the selected account's orders, one order's detail and the
 * cancel flow. All of it lives in DcaController (background) so a cancel keeps
 * going when the popup is destroyed by the Sign window; this hook dispatches
 * and polls while `enabled` (the DCA view is on screen).
 */
const useDcaHistory = ({ enabled }: { enabled: boolean }) => {
  const { dispatch } = useBackgroundService()
  const state = useControllerState('DcaController')
  const { account } = useSelectedAccountControllerState()
  const { keys } = useKeystoreControllerState()
  const { networks } = useNetworksControllerState()
  const owner = account?.addr ?? null

  useEffect(() => {
    dispatch({ type: 'INIT_CONTROLLER_STATE', params: { controller: 'DcaController' } })
    dispatch({ type: 'DCA_CONTROLLER_LOAD_CONFIG', params: {} })
  }, [dispatch])

  const isAvailable = state?.configStatus === 'ready'

  const loadOrders = useCallback(
    (silent = false) => {
      if (owner) dispatch({ type: 'DCA_CONTROLLER_LOAD_ORDERS', params: { owner, silent } })
    },
    [dispatch, owner]
  )

  useEffect(() => {
    if (!enabled || !isAvailable || !owner) return undefined
    loadOrders()
    const id = setInterval(() => loadOrders(true), LIST_POLL_MS)
    return () => clearInterval(id)
  }, [enabled, isAvailable, owner, loadOrders])

  // The controller holds one owner's list; drop it while another account loads.
  const isOwnList = same(state?.ordersOwner, owner)
  const orders: DcaOrder[] = isOwnList ? (state?.orders ?? []) : []
  const pendingSubmissions: DcaPendingSubmission[] = isOwnList
    ? (state?.pendingSubmissions ?? [])
    : []
  const ordersStatus: DcaOrdersStatus = isOwnList ? (state?.ordersStatus ?? 'idle') : 'loading'

  const detailOrderId: string | null = state?.detailOrderId ?? null
  const detailStatus: DcaDetailStatus = state?.detailStatus ?? 'idle'
  const detail: DcaOrderDetail | null = state?.detail ?? null
  const isDetailOpen = enabled && !!detailOrderId

  const loadDetail = useCallback(
    (orderId: string, silent = false) => {
      if (owner)
        dispatch({
          type: 'DCA_CONTROLLER_LOAD_ORDER_DETAIL',
          params: { owner, orderId, silent }
        })
    },
    [dispatch, owner]
  )

  // Poll the open order while it can still change: it runs on a schedule, and
  // right after creation it is not indexed yet.
  const detailIsLive =
    detailStatus === 'indexing' ||
    detailStatus === 'loading' ||
    detail?.status === 'ACTIVE' ||
    detail?.status === 'PAUSED'
  useEffect(() => {
    if (!isDetailOpen || !detailIsLive || !detailOrderId) return undefined
    const id = setInterval(() => loadDetail(detailOrderId, true), DETAIL_POLL_MS)
    return () => clearInterval(id)
  }, [isDetailOpen, detailIsLive, detailOrderId, loadDetail])

  const openDetail = useCallback((orderId: string) => loadDetail(orderId), [loadDetail])

  const cancelStatus: DcaCancelStatus = state?.cancelStatus ?? 'idle'

  const closeDetail = useCallback(() => {
    dispatch({ type: 'DCA_CONTROLLER_CLOSE_ORDER_DETAIL' })
    // A cancel still in flight keeps running; only a settled one is cleared.
    if (cancelStatus !== 'preparing' && cancelStatus !== 'running')
      dispatch({ type: 'DCA_CONTROLLER_RESET_CANCEL' })
  }, [dispatch, cancelStatus])

  const cancelOrder = useCallback(
    (orderId: string) => {
      if (owner) dispatch({ type: 'DCA_CONTROLLER_CANCEL_ORDER', params: { owner, orderId } })
    },
    [dispatch, owner]
  )

  const resetCancel = useCallback(() => {
    dispatch({ type: 'DCA_CONTROLLER_RESET_CANCEL' })
    // The order changed on-chain: refresh what is on screen.
    loadOrders(true)
    if (detailOrderId) loadDetail(detailOrderId, true)
  }, [dispatch, loadOrders, loadDetail, detailOrderId])

  // Cancelling signs transactions: not for view-only or smart accounts (DCA is EOA-only).
  const canManage =
    !!account &&
    !isSmartAccount(account) &&
    !(!account.safeCreation && getIsViewOnly(keys, account.associatedKeys))

  const explorerUrl = useMemo(
    () => networks.find((n) => Number(n.chainId) === DCA_CHAIN_ID)?.explorerUrl ?? null,
    [networks]
  )

  return {
    isAvailable,
    owner,
    orders,
    pendingSubmissions,
    ordersStatus,
    ordersError: (state?.ordersError ?? null) as DcaErrorCode | null,
    tokens: (state?.tokens ?? {}) as Record<string, DcaToken>,
    reload: () => loadOrders(),
    detailOrderId: enabled ? detailOrderId : null,
    detailStatus,
    detail,
    executions: (state?.executions ?? []) as DcaExecution[],
    detailError: (state?.detailError ?? null) as DcaErrorCode | null,
    openDetail,
    closeDetail,
    cancelOrderId: (state?.cancelOrderId ?? null) as string | null,
    cancelStatus,
    cancelSteps: (state?.cancelSteps ?? []) as DcaFlowStep[],
    cancelSummary: (state?.cancelSummary ?? null) as DcaCancelSummary | null,
    cancelError: (state?.cancelError ?? null) as DcaErrorCode | null,
    cancelOrder,
    resetCancel,
    canManage,
    explorerUrl
  }
}

export type UseDcaHistoryReturn = ReturnType<typeof useDcaHistory>

export default useDcaHistory
